/** @format */

import {ChangeEvent, forwardRef} from 'react';

import {styled} from '@mui/material/styles';

import {Box, MenuItem, Select} from '@mui/material';
import clsx from 'clsx';

import {AccountsNotificationChannelType} from 'graphql/generated/types';

import ErrorMessage from '../ErrorMessage';

const PREFIX = 'SecondarySelect';

const classes = {
  select: `${PREFIX}-select`,
  menuItem: `${PREFIX}-menuItem`,
  icon: `${PREFIX}-icon`,
};

interface StyledBoxProps {
  width?: number;
}

const StyledBox = styled(Box, {
  shouldForwardProp: prop => prop !== 'width',
})<StyledBoxProps>(({theme, width}) => ({
  width: width !== undefined ? `${width}px` : undefined,
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',

  [`& .${classes.select}`]: {
    flexGrow: 1,
    width: (props: Props) => props.width,
    color: theme.palette.text.primary,
    '& .MuiSelect-select': {
      minHeight: '24px',
      display: 'flex',
      alignItems: 'center',
      borderRadius: 8,
      border: `1px solid ${theme.palette.figma_gray_middle?.main}`,
      backgroundColor: theme.palette.secondary.main,
      paddingBottom: theme.spacing(3),
      paddingTop: theme.spacing(3),
      paddingRight: theme.spacing(4),
      paddingLeft: theme.spacing(4),
      color: theme.palette.text.secondary,
    },
    '& .MuiSelect-icon': {
      right: 6,
    },
  },

  [`& .${classes.menuItem}`]: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.text.secondary,
    '&:hover': {
      backgroundColor: '#F0F0F0',
    },
    '&.Mui-selected': {
      backgroundColor: '#E0E0E0',
    },
  },

  [`& .${classes.icon}`]: {
    position: 'absolute',
    left: '16px',
    top: '14px',
    bottom: 0,
    margin: 'auto',
    height: '20px',
  },
}));

type Option = {
  value: string;
  text: string;
};

type Props = {
  disabled?: boolean;
  options: Option[];
  selectClassName?: string;
  icon?: React.ReactNode;
  value: string;
  placeholder?: string | null;
  width?: number;
  onSelect: (value: AccountsNotificationChannelType) => void;
  'aria-label'?: string;
  error?: string | null;
};

const SecondarySelect = forwardRef((props: Props, ref) => {
  const {disabled = false, error} = props;

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    props.onSelect(event.target.value as AccountsNotificationChannelType);
  };

  return (
    <StyledBox width={props.width}>
      {props.icon && <Box className={classes.icon}>{props.icon} </Box>}
      <Select
        {...props}
        inputRef={ref}
        className={clsx(classes.select, props.selectClassName)}
        disabled={disabled}
        value={props.value}
        placeholder={props.placeholder || undefined}
        aria-label={props['aria-label']}
        error={Boolean(error)}
        // @ts-expect-error MUI Select typing mismatch for custom styled wrapper
        onChange={handleChange}
      >
        {props.options.map((v, i) => (
          <MenuItem key={i} className={classes.menuItem} value={v.value} data-testid={'option'}>
            {v.text}
          </MenuItem>
        ))}
      </Select>
      {error && <ErrorMessage text={error} />}
    </StyledBox>
  );
});

SecondarySelect.displayName = 'SecondarySelect';

export default SecondarySelect;
